import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Formik } from 'formik';
import { v4 as UIDV4 } from 'uuid';
import { format, parseISO } from 'date-fns';
import { Box } from '@mui/system';
import MainCard from 'components/MainCard';
import CircularLoader from 'components/CircularLoader';
import InvoiceForm from 'components/InvoiceForm';
import {
  useGetInvoice,
  useGetInvoiceMaster,
  handlerCustomerTo,
  handlerPreview,
} from 'api/invoice';
import { openSnackbar } from 'api/snackbar';
import { fetchInvoiceByInvoiceNumber, fetchMedicine, fetchNonMedicineItems, sellMedicines, sellMedicineToSupplier, cancelInvoiceItems, bulkCancelInvoiceItems, fetchDoctors, fetchLatestPrescriptionForPatient, fetchUserDiscountInfo, fetchHospitalDetails, editInvoice } from 'utils/apiHelper';
import { extractErrorMessage, ERROR_MESSAGES } from 'utils/errorHandler';
import useAuth from 'hooks/useAuth';
import UniversalPrintModal from 'components/print/UniversalPrintModal';
import InvoicePrintPreviewModal from 'components/print/InvoicePrintPreviewModal';
import PharmacyInvoiceThermalContent from './PharmacyInvoiceThermalContent';
import PharmacyInvoiceA4Content from './PharmacyInvoiceA4Content';
import OTPApprovalModal from 'components/OTPApprovalModal';
import PrescriptionAutoFillModal from 'components/CustomMedicineAlertModal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SaleInvoiceA4Content from './SaleInvoiceA4Content';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import BulkCancelInvoiceThermalContent from './BulkCancelInvoiceThermalContent';
import { calculatePharmacyInvoiceRounding, resolvePharmacyInvoiceTotals } from './utils';
import {
  normalizeHospitalDetails,
  summarizeInclusiveGst,
  summarizePurchaseBillGst,
  normalizeSupplierDetails,
  getItemGstPercentage,
  calculateInclusiveLineGst
} from 'utils/gst';
import { useHospitalDetails } from 'api/cutsom-hooks';
import { fetchPatientWalletStatement } from 'api/wallet';
import { getInvoiceBillingProviderValue } from 'utils/invoiceBillingProvider';

async function fetchAllItems(search) {
  const [meds, nonMeds] = await Promise.all([
    fetchMedicine(search, true),
    fetchNonMedicineItems(search),
  ]);
  // Filter out any disabled medicines (additional safety check)
  const activeMeds = Array.isArray(meds) ? meds.filter(med => med.is_active !== false) : meds;
  return [...activeMeds, ...nonMeds];
}
const defaultInitialValues = {
  date: new Date(),
  due_date: new Date(),
  customerInfo: {},
  cashierInfo: {},
  supplier_id: '',
  invoice_detail: [
    { id: UIDV4(), name: '', qty: 1, selling_price: 1, buying_price: 0, strips: 0, pcs: 0, cancel_quantity: 0, discount_in_perc: 0, gst_percentage: 0 }
  ],
  discount: 0,
  tax: 0,
  notes: '',
  status: '',
  payment_method: '',
  cash_amount: '',
  online_amount: '',
  internal_notes: '',
  invoice_number: '',
  store_id: '',
  doctor_id: '',
  cancellation_type: '',
  cancellation_notes: '',
  is_gst_bill: true,
};

const PharmacyInvoicePage = ({
  variant = "pharmacy",
  // For Sale Bill
  setWholesalerEditModalOpen,
  setWholesalerModalOpen,
  setSelectedWholesaler,
  selectedWholesaler,
  wholesalersList,
}) => {
  const { roles, isAdmin, user } = useAuth();
  const { invoice } = useGetInvoice();
  const { invoiceMasterLoading, invoiceMaster } = useGetInvoiceMaster();
  const [initialValues, setInitialValues] = useState(defaultInitialValues);
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams()[0];
  const invoiceId = searchParams.get('invoiceId');
  const inPlaceEdit = searchParams.get('inPlaceEdit');
  const isInPlaceEditMode = inPlaceEdit === 'true';
  const [openTokenModal, setOpenTokenModal] = useState(false);
  const [saleBillPrintModal, setSaleBillPrintModal] = useState(false);
  const [medicineOrderResponse, setMedicineOrderResponse] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [billingProvider, setBillingProvider] = useState(null);
  const [originalBillingProvider, setOriginalBillingProvider] = useState('self');
  const [autoSendWhatsAppKey, setAutoSendWhatsAppKey] = useState(null);
  const [originalInvoiceData, setOriginalInvoiceData] = useState(null);
  const [walletRefundNotice, setWalletRefundNotice] = useState(null);
  const navigate = useNavigate();
  const isSaleBill = variant === "sale";
  const [saleBillItem, setSaleBillItem] = useState({});

  const [grandTotalInput, setGrandTotalInput] = useState(null);
  const [isGrandTotalMode, setIsGrandTotalMode] = useState(false);
  const isMainDiscountEditingRef = useRef(false);
  const lastSyncedDiscountRef = useRef(null);
  
  // OTP Approval states
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpMessageId, setOtpMessageId] = useState(null);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [, setSelectedDoctor] = useState('');
  const [showDoctorSelection, setShowDoctorSelection] = useState(true);
  const [pendingPayload, setPendingPayload] = useState(null);
  
  // OTP states for cancellation
  const [cancellationOtpModalOpen, setCancellationOtpModalOpen] = useState(false);
  const [cancellationOtpToken, setCancellationOtpToken] = useState(null);
  const [cancellationOtpMessageId, setCancellationOtpMessageId] = useState(null);
  const [cancellationIsOTPSent, setCancellationIsOTPSent] = useState(false);
  const [cancellationShowDoctorSelection, setCancellationShowDoctorSelection] = useState(false);
  const [, setCancellationSelectedDoctor] = useState(null);
  const [pendingCancellationPayload, setPendingCancellationPayload] = useState(null);

  const [oldBillOtpModalOpen, setOldBillOtpModalOpen] = useState(false);
  const [oldBillOtpMessageId, setOldBillOtpMessageId] = useState(null);
  const [oldBillOtpSubmitting, setOldBillOtpSubmitting] = useState(false);
  const [pendingOldBillPayload, setPendingOldBillPayload] = useState(null);
  const [oldBillOtpIsBulk, setOldBillOtpIsBulk] = useState(false);
  const [oldBillBulkContext, setOldBillBulkContext] = useState(null);

  const [doctors, setDoctors] = useState([]);
  const [, setIsLoadingDoctors] = useState(false);

  const [customMedicineModalOpen, setCustomMedicineModalOpen] = useState(false);
  const [pendingDbMedicines, setPendingDbMedicines] = useState([]);
  const [pendingCustomMedicines, setPendingCustomMedicines] = useState([]);
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState(null);
  const [pendingAppointmentDate, setPendingAppointmentDate] = useState('');

  const [bulkCancelItem, setBulkCancelItem] = useState({});
  const [bulkCancelPrintModal, setBulkCancelPrintModal] = useState(false);
  const [salePriceType, setSalePriceType] = useState('selling_price');

  // User-specific discount states
  const [userDiscount, setUserDiscount] = useState(null);
  const [hasUserDiscount, setHasUserDiscount] = useState(false);

  const getSelectedStoreObject = () => {
    try {
      return JSON.parse(window.localStorage.getItem('selectedStore') || 'null') || {};
    } catch (error) {
      return {};
    }
  };

  const modalSaleBillCloseHandler = () => {
    setSaleBillPrintModal(false);
    navigate(-1);
  };

  const modalBulkCancelCloseHandler = () => {
    setBulkCancelPrintModal(false);
    resetFormData();
  };

  const resetFormData = () => {
      setInitialValues({
        ...defaultInitialValues,
        invoice_detail: [{ id: UIDV4(), name: '', qty: 1, price: 1, strips: 0, pcs: 0, cancel_quantity: 0, discount_in_perc: 0, gst_percentage: 0 }]
      });
      
      setIsGrandTotalMode(false);
      setGrandTotalInput(null);
      
      setMedicineOrderResponse(null);
      setCustomerInfo(null);
      setAutoSendWhatsAppKey(null);
      
      // Reset user discount states
      setUserDiscount(null);
      setHasUserDiscount(false);
      
      // Reset OTP states
      resetOTPStates();
  };
  
  const handleModalClose = () => {
    setOpenTokenModal(false);
    if (!isCancellationMode) {
      resetFormData();
    }
  };

  // Function to reset all OTP-related states
  const resetOTPStates = () => {
    setOtpModalOpen(false);
    setOtpToken(null);
    setOtpMessageId(null);
    setIsOTPSent(false);
    setSelectedDoctor('');
    setShowDoctorSelection(true);
    setPendingPayload(null);
  };

  // Function to reset cancellation OTP states
  const resetCancellationOTPStates = () => {
    setCancellationOtpModalOpen(false);
    setCancellationOtpToken(null);
    setCancellationOtpMessageId(null);
    setCancellationIsOTPSent(false);
    setCancellationShowDoctorSelection(false);
    setCancellationSelectedDoctor(null);
    setPendingCancellationPayload(null);
  };

  const resetOldBillOtpStates = () => {
    setOldBillOtpModalOpen(false);
    setOldBillOtpMessageId(null);
    setOldBillOtpSubmitting(false);
    setPendingOldBillPayload(null);
    setOldBillOtpIsBulk(false);
    setOldBillBulkContext(null);
  };

  const completeCancellation = async (result, cancellationItems) => {
    const cancelledItems = cancellationItems
      .filter(item => Number(item.cancel_quantity) > 0)
      .map(item => ({
        id: item.id,
        cancel_quantity: Number(item.cancel_quantity),
        name: item.name || 'Unknown Item',
        quantity: Number(item.quantity || item.cancel_quantity),
        selling_price: Number(item.selling_price || item.price || 0)
      }));
    const params = new URLSearchParams({
      showCancellationPrint: 'true',
      invoiceNumber: originalInvoiceData.invoice_number,
      cancelledItems: JSON.stringify(cancelledItems),
      type: 'pharmacy'
    });
    const destination = `/invoice/list?${params.toString()}`;

    if (originalInvoiceData?.payment_method?.toLowerCase() === 'wallet') {
      let currentBalance = result?.wallet_refund?.current_balance;
      try {
        const statement = await fetchPatientWalletStatement(originalInvoiceData.customer?.id);
        currentBalance = statement.current_balance;
      } catch (error) {
        // The cancellation response still contains the committed balance.
      }
      const creditedAmount = result?.wallet_refund?.amount ??
        Math.max(0, Number(originalInvoiceData.total_amount || 0) - Number(result?.new_total || 0));
      setWalletRefundNotice({ creditedAmount, currentBalance, destination });
      return;
    }

    setTimeout(() => navigate(destination), 1500);
  };

  const closeWalletRefundNotice = () => {
    const destination = walletRefundNotice?.destination;
    setWalletRefundNotice(null);
    if (destination) navigate(destination);
  };

  const buildBulkCancelEnhancedResponse = (result, bulkItems, customerInfo) => {
    const cancelledResult = result?.cancelled || [];
    const totalAmount = cancelledResult[cancelledResult.length - 1]?.total_amount_cancelled || 0;
    const cancelledItemsAmountMap = {};
    const cancelledItemsQtyMap = {};
    const invoiceIdsSet = new Set();

    cancelledResult.slice(0, -1).forEach(cancelledItem => {
      if (cancelledItem.item_id) {
        const itemId = String(cancelledItem.item_id);
        if (cancelledItem.cancelled_item_amount !== undefined) {
          cancelledItemsAmountMap[itemId] = (cancelledItemsAmountMap[itemId] || 0) + Number(cancelledItem.cancelled_item_amount || 0);
        }
        if (cancelledItem.cancelled_qty !== undefined) {
          cancelledItemsQtyMap[itemId] = (cancelledItemsQtyMap[itemId] || 0) + Number(cancelledItem.cancelled_qty || 0);
        }
      }
      if (cancelledItem.invoice_id) {
        invoiceIdsSet.add(cancelledItem.invoice_id);
      }
    });

    return {
      invoiceData: {
        items: bulkItems.map(item => {
          const itemId = String(item.id);
          return {
            id: item.id,
            name: item.name,
            quantity: cancelledItemsQtyMap[itemId] || item.quantity,
            amount: cancelledItemsAmountMap[itemId] || 0
          };
        }),
        store: JSON.parse(window.localStorage.getItem('selectedStore')) || {},
        created_at: new Date(),
        totalAmount,
        cancelledInvoiceIds: Array.from(invoiceIdsSet).sort()
      },
      customerInfo
    };
  };

  const handleOldBillOtpApproval = async (otpData) => {
    try {
      setOldBillOtpSubmitting(true);
      const finalPayload = {
        ...pendingOldBillPayload,
        cancellation_message_id: oldBillOtpMessageId,
        cancellation_otp_input: otpData.otp_input
      };

      if (oldBillOtpIsBulk) {
        const result = await bulkCancelInvoiceItems(finalPayload);
        openSnackbar({
          open: true,
          message: 'Invoice cancelled successfully!',
          variant: 'alert',
          alert: { color: 'success' },
        });
        setBulkCancelItem(buildBulkCancelEnhancedResponse(result, oldBillBulkContext?.items || [], oldBillBulkContext?.customerInfo));
        setBulkCancelPrintModal(true);
        resetOldBillOtpStates();
        return;
      }

      const result = await cancelInvoiceItems(originalInvoiceData.invoice_number, finalPayload);
      openSnackbar({
        open: true,
        message: 'Items cancelled successfully!',
        variant: 'alert',
        alert: { color: 'success' },
      });

      const cancellationItems = pendingOldBillPayload?.cancellation?.pharmacy || [];
      resetOldBillOtpStates();
      await completeCancellation(result, cancellationItems);
    } catch (err) {
      console.error('Old Bill Cancellation OTP error:', err);
      openSnackbar({
        open: true,
        message: extractErrorMessage(err) || 'Failed to cancel items with OTP',
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setOldBillOtpSubmitting(false);
    }
  };

  const handleCloseCustomMedicineModal = () => {
    setCustomMedicineModalOpen(false);
    setPendingDbMedicines([]);
    setPendingCustomMedicines([]);
    setPendingSetFieldValue(null);
    setPendingAppointmentDate('');
  };

  const handleConfirmPrescriptionAdd = (selectedDbMedicines, selectedCustomMedicines) => {
    if (!pendingSetFieldValue) {
      handleCloseCustomMedicineModal();
      return;
    }

    const newInvoiceItems = [];

    selectedDbMedicines.forEach((medicine) => {
      const hasConfiguredDiscount =
        medicine.hasConfiguredDiscount === true ||
        (medicine.discount !== null && medicine.discount !== undefined && medicine.discount !== '');
      const parsedMedicineDiscount = Number(medicine.discount);
      const medicineDiscount = Number.isFinite(parsedMedicineDiscount) ? parsedMedicineDiscount : 0;
      const finalDiscount = hasConfiguredDiscount ? medicineDiscount : (hasUserDiscount && userDiscount ? userDiscount : 0);
      
      newInvoiceItems.push({
        id: UIDV4(),
        name: medicine.displayName || medicine.name,
        hsn_code: medicine.hsn_code || '',
        medicineId: medicine.medicineId,
        nonMedicineId: medicine.nonMedicineId,
        type: medicine.type,
        qty: medicine.quantity || 1,
        selling_price: medicine.selling_price || 0,
        buying_price: medicine.buying_price || 0,
        strips: 0,
        pcs: 0,
        cancel_quantity: 0,
        discount_in_perc: finalDiscount,
        gst_percentage: getItemGstPercentage(medicine),
        packing: medicine.packing || '',
        loose_quantity: medicine.loose_quantity || 0,
      });
    });

    selectedCustomMedicines.forEach((medicine) => {
      const dosageStr = medicine.dosageStr ?? typeof medicine.dosage === 'object' 
        ? `${medicine.dosage.morning || 0}-${medicine.dosage.noon || 0}-${medicine.dosage.evening || 0}-${medicine.dosage.night || 0}`
        : medicine.dosage || '';
      
      newInvoiceItems.push({
        id: UIDV4(),
        name: '',
        medicineId: null,
        qty: medicine.quantity || 1,
        selling_price: 0,
        buying_price: 0,
        strips: 0,
        pcs: 0,
        cancel_quantity: 0,
        discount_in_perc: 0,
        gst_percentage: 0,
        packing: '',
        loose_quantity: 0,
        isCustomPlaceholder: true,
        customMedicineName: medicine.name || medicine.medicine_name,
        customDosage: dosageStr,
        customDuration: medicine.duration || '',
        customFrequency: medicine.frequency || 'Daily',
        customInstructions: medicine.instructions || ''
      });
    });

    if (newInvoiceItems.length > 0) {
      newInvoiceItems.push({
        id: UIDV4(),
        name: '',
        qty: 1,
        selling_price: 1,
        buying_price: 0,
        strips: 0,
        pcs: 0,
        cancel_quantity: 0,
        discount_in_perc: 0,
        gst_percentage: 0
      });

      pendingSetFieldValue('invoice_detail', newInvoiceItems);

      const dbCount = selectedDbMedicines.length;
      const customCount = selectedCustomMedicines.length;
      
      if (customCount > 0) {
        openSnackbar({
          open: true,
          message: `${dbCount} medicine(s) added. ${customCount} custom medicine(s) need manual selection.`,
          variant: 'alert',
          alert: { color: 'warning' }
        });
      } else {
        openSnackbar({
          open: true,
          message: `${dbCount} medicine(s) added to invoice from prescription.`,
          variant: 'alert',
          alert: { color: 'success' }
        });
      }
    }
    
    handleCloseCustomMedicineModal();
  };

  const calculateRoundOff = (subtotal) => {
    return subtotal < 5000 ? 5 : 10;
  };

  const calculateRoundOffAmount = (subtotal, grandTotal) => {
    const maxRoundOff = calculateRoundOff(subtotal);
    const difference = Math.abs(subtotal - grandTotal);
    
    if (difference <= maxRoundOff) {
      const roundOffAmount = subtotal - grandTotal;
      return {
        amount: Math.abs(roundOffAmount),
        isPositive: roundOffAmount < 0
      };
    }
    
    return { amount: 0, isPositive: false };
  };

  const calculateDiscountFromTotal = (subtotal, grandTotal) => {
    const roundOff = calculateRoundOff(subtotal);
    const maxRoundOffReduction = subtotal - roundOff; // e.g., 200 - 5 = 195
    
    if (grandTotal >= maxRoundOffReduction && grandTotal <= subtotal) {
      return 0;
    }
    
    if (grandTotal < maxRoundOffReduction) {
      const discountAmount = subtotal - grandTotal;
      const discountPercentage = (discountAmount / subtotal) * 100;
      return Math.max(0, Math.min(100, discountPercentage));
    }
    
    const discountAmount = subtotal - grandTotal;
    const discountPercentage = (discountAmount / subtotal) * 100;
    return Math.max(0, Math.min(100, discountPercentage));
  };

  // OTP Approval functions
  const handleSendOTP = async (doctorId) => {
    try {
      setSelectedDoctor(doctorId);
      
      // Send the pending payload with doctor selection
      const payloadWithDoctor = {
        ...pendingPayload,
        approver: doctorId
      };
      
      const response = await sellMedicines(payloadWithDoctor);
      
      if (response.message_id && response.message) {
        setOtpToken(response.message_id);
        setOtpMessageId(response.message_id);
        setIsOTPSent(true);
        
        openSnackbar({
          open: true,
          message: 'OTP sent successfully for approval',
          variant: 'alert',
          alert: { color: 'success' }
        });
      } else {
        throw new Error('Invalid OTP response');
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: 'Failed to send OTP for approval',
        variant: 'alert',
        alert: { color: 'error' }
      });
      throw error;
    }
  };

  const handleOTPApproval = async (otpData) => {
    try {
      // Send the final payload with OTP
      const finalPayload = {
        ...pendingPayload,
        approver: otpData.approver,
        message_id: otpData.message_id,
        otp_input: otpData.otp_input
      };
      
      const result = await sellMedicines(finalPayload);
      
      // Process the successful response
      const isGstBillSelected = finalPayload.is_gst_bill ?? true;
      const pharmacyItems = (result?.items?.pharmacy || []).map((item) => ({
        ...item,
        gst_percentage: isGstBillSelected ? getItemGstPercentage(item) : 0
      }));
      const activeItems = pharmacyItems.filter(item => !item.status || item.status === 'active');
      
      const subtotal = activeItems.reduce((sum, item) => sum + (Number(item.selling_price || 0) * Number(item.quantity || 1)), 0);
      
      const totalDiscount = activeItems.reduce((sum, item) =>
        sum + ((Number(item.selling_price || 0) * Number(item.quantity || 1)) * Number(item.discount_in_perc || 0)) / 100, 0);
      
      const pharmacyGstSummary = summarizeInclusiveGst({
        items: activeItems,
        gstEnabled: isGstBillSelected,
        mode: 'cgst_sgst',
        summarySource: result
      });
      const resolvedTotals = resolvePharmacyInvoiceTotals({
        netTotal: pharmacyGstSummary.totalAmount
      });

      const resolvedCustomer =
        result?.customer && typeof result.customer === 'object'
          ? result.customer
          : customerInfo || pendingPayload.customer_id;
      const enhancedResponse = {
        ...result,
        payment_method: result?.payment_method || pendingPayload.payment_method,
        customer: resolvedCustomer,
        items: {
          ...result?.items,
          pharmacy: pharmacyItems
        },
        actualSubtotal: subtotal,
        actualDiscount: totalDiscount,
        roundOff: resolvedTotals.roundOffDisplay,
        isPositiveRoundOff: resolvedTotals.isPositiveRoundOff,
        finalTotal: resolvedTotals.grandTotal,
        total_amount: resolvedTotals.grandTotal,
        round_off: resolvedTotals.roundOff,
        pharmacy_gst_summary: pharmacyGstSummary
      };
      
      setMedicineOrderResponse(enhancedResponse);
      setCustomerInfo(resolvedCustomer);
      setAutoSendWhatsAppKey(`pharmacy-invoice:${enhancedResponse.invoice_number || enhancedResponse.id}:${Date.now()}`);
      
      openSnackbar({
        open: true,
        message: 'Pharmacy order created successfully!',
        variant: 'alert',
        alert: { color: 'success' }
      });
      
      setOpenTokenModal(true);
      setOtpModalOpen(false);
      
      // Reset OTP states after successful completion
      resetOTPStates();
      
    } catch (error) {
      openSnackbar({
        open: true,
        message: 'Failed to create pharmacy order with OTP',
        variant: 'alert',
        alert: { color: 'error' }
      });
    }
  };

  // OTP handlers for cancellation
  const handleCancellationSendOTP = async (doctorId) => {
    try {
      setCancellationSelectedDoctor(doctorId);
      
      // Send the pending cancellation payload with doctor selection
      const payloadWithDoctor = {
        ...pendingCancellationPayload,
        approver: doctorId
      };
      
      const response = await cancelInvoiceItems(originalInvoiceData.invoice_number, payloadWithDoctor);
      
      if (response.message_id && response.message) {
        setCancellationOtpToken(response.message_id);
        setCancellationOtpMessageId(response.message_id);
        setCancellationIsOTPSent(true);
        
        openSnackbar({
          open: true,
          message: 'OTP sent successfully for cancellation approval',
          variant: 'alert',
          alert: { color: 'success' }
        });
      } else {
        throw new Error('Invalid OTP response');
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: 'Failed to send OTP for cancellation approval',
        variant: 'alert',
        alert: { color: 'error' }
      });
      throw error;
    }
  };

  const handleCancellationOTPApproval = async (otpData) => {
    try {
      // Send the final cancellation payload with OTP
      const finalPayload = {
        ...pendingCancellationPayload,
        approver: otpData.approver,
        message_id: otpData.message_id,
        otp_input: otpData.otp_input
      };
      
      const result = await cancelInvoiceItems(originalInvoiceData.invoice_number, finalPayload);
      
      openSnackbar({
        open: true,
        message: 'Items cancelled successfully!',
        variant: 'alert',
        alert: { color: 'success' },
      });

      await completeCancellation(result, pendingCancellationPayload.cancellation.pharmacy);
      
      setCancellationOtpModalOpen(false);
      resetCancellationOTPStates();
      
    } catch (error) {
      openSnackbar({
        open: true,
        message: 'Failed to cancel items with OTP',
        variant: 'alert',
        alert: { color: 'error' }
      });
    }
  };

  const generateBulkCancelUnfulfilledError = (pharmacyItems, unfulfilledData) =>  {
    const pharmacyItemsMap = {};
    pharmacyItems.forEach(item => {
      pharmacyItemsMap[item.medicine_id || item.non_medicine_item_id] = item;
    });
    const errorMessages = unfulfilledData.map(item => {
      const pharmacyItem = pharmacyItemsMap[item.item_id];
      return `Item "${pharmacyItem.name}" is unfulfilled in the required quantity (${item.unfulfilled_cancel_qty}).`;
    });
    return errorMessages.join('\n');
  }

  const isLoader = loading || invoiceMasterLoading || invoiceMaster === undefined;
  const isCancellationMode = !!invoiceId && !isInPlaceEditMode;

  function mapInvoiceToFormik(data) {
    const allPharmacyItems = data.items.pharmacy || [];

    // Check if there are any cancelled items (indicates true cancellation scenario)
    const hasCancelledItems = allPharmacyItems.some(item => item.status === 'cancelled');
    
    let activeItems;

    if (hasCancelledItems) {
      // True cancellation scenario - group by ID to calculate net quantities
      const itemsMap = {};
      allPharmacyItems.forEach(item => {
        const itemId = item.id;
        if (!itemsMap[itemId]) {
      itemsMap[itemId] = {
        id: itemId,
        name: item.name,
        selling_price: parseFloat(item.selling_price || item.price || 0),
        activeQuantity: 0, // Quantity from active records
        cancelledQuantity: 0, // Sum of cancelled quantities
        created_at: item.created_at,
        created_by: item.created_by,
        discount_in_perc: item.discount_in_perc || 0,
        batch_number: item.batch_number,
        expiry_date: item.expiry_date,
        manufacturing_date: item.manufacturing_date,
        batches: [],
        type: item.type
      };
        }

        if (item.status === 'active') {
          // Active record shows remaining quantity after cancellation
          itemsMap[itemId].activeQuantity += Number(item.quantity);
          if (Array.isArray(item.batches) && item.batches.length > 0) {
            itemsMap[itemId].batches.push(...item.batches);
          } else if (item.batch_number) {
            itemsMap[itemId].batches.push({
              batch_number: item.batch_number,
              expiry_date: item.expiry_date,
              quantity: item.quantity
            });
          }
        } else if (item.status === 'cancelled') {
          // Sum up all cancelled quantities
          itemsMap[itemId].cancelledQuantity += Number(item.quantity);
        }
      });

      // Include all items (including cancelled ones) but mark them appropriately
      activeItems = Object.values(itemsMap)
        .map(item => {
          // Calculate net quantity (active - cancelled)
          const netQuantity = item.activeQuantity - item.cancelledQuantity;
          const isCancelled = netQuantity <= 0;
          return {
            id: UIDV4(),
            name: item.name,
            hsn_code: item.hsn_code || '',
            qty: isCancellationMode ? netQuantity : item.activeQuantity,
            selling_price: item.selling_price,
            buying_price: item.buying_price || 0,
            strips: 0,
            pcs: 0,
            ...((item.type && typeof item.type === 'string' && item.type.toLowerCase().includes('non'))
              ? { nonMedicineId: item.id }
              : { medicineId: item.id }),
            cancel_quantity: isCancellationMode ? 0 : item.cancelledQuantity,
            originalQuantity: item.activeQuantity,
            cancelledQuantity: item.cancelledQuantity,
            activeQuantity: item.activeQuantity,
            discount_in_perc: item.discount_in_perc || 0,
            gst_percentage: getItemGstPercentage(item),
            net_price: parseFloat((item.selling_price * (1 - (item.discount_in_perc / 100))).toFixed(2)),
            created_at: item.created_at,
            created_by: item.created_by,
            batches: item.batches,
            batch_number: item.batch_number,
            expiry_date: item.expiry_date,
            isCancelled: isCancelled // Add flag to identify cancelled items
          };
        });

    } else {
      // No cancellations - show all items individually (including duplicates)
      activeItems = allPharmacyItems
        .filter(item => item.status === 'active')
        .map(item => ({
          id: UIDV4(),
          name: item.name,
          qty: Number(item.quantity),
          selling_price: parseFloat(item.selling_price || item.price || 0),
          buying_price: parseFloat(item.buying_price || 0),
          strips: 0,
          pcs: 0,
          ...((item.type && typeof item.type === 'string' && item.type.toLowerCase().includes('non'))
            ? { nonMedicineId: item.id }
            : { medicineId: item.id }),
          cancel_quantity: 0,
          originalQuantity: Number(item.quantity),
          cancelledQuantity: 0,
          activeQuantity: Number(item.quantity),
          discount_in_perc: item.discount_in_perc || 0,
          gst_percentage: getItemGstPercentage(item),
          net_price: parseFloat((item.selling_price * (1 - (item.discount_in_perc / 100))).toFixed(2)),
          created_at: item.created_at,
          created_by: item.created_by,
          batches: item.batches,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date
        }));
    }

    // Continue with the rest of the function using the processed activeItems

    const originalSubtotal = Number(data.original_amount || 0);
    const originalDiscount = allPharmacyItems[0]?.discount_in_perc ? parseFloat(allPharmacyItems[0].discount_in_perc) : 0;
    const originalDiscountAmount = (originalDiscount * originalSubtotal) / 100;
    const originalRoundOff = Number(data.round_off || 0);
    
    const totalAmount = Number(data.total_amount || 0);
    const isPositiveRoundOff = originalSubtotal > totalAmount;
    
    const calculatedGrandTotal = totalAmount;

    const shouldBeInGrandTotalMode = Math.abs(originalRoundOff) > 0;

    return {
      date: data.created_at ? parseISO(data.created_at) : new Date(),
      due_date: data.due_date ? parseISO(data.due_date) : new Date(),
      customerInfo: data.customer || {},
      cashierInfo: {},
      invoice_detail: activeItems,
      discount: originalDiscount,
      tax: 0,
      notes: data.notes || '',
      status: data.status || '',
      payment_method: data.payment_method || '',
      cash_amount: data.cash_amount ?? '',
      online_amount: data.online_amount ?? '',
      internal_notes: data.notes || '',
      invoice_number: data.invoice_number || '',
      store_id: data.store_id || '',
      doctor_id: (() => {
        try {
          if (data.other_details) {
            const additionalInfo = typeof data.other_details === 'string' 
              ? JSON.parse(data.other_details) 
              : data.other_details;
            return additionalInfo.doctor_id || '';
          }
        } catch (e) {
          console.error('Error parsing additional_info:', e);
        }
        return '';
      })(),
      cancellation_type: '',
      cancellation_notes: '',
      is_gst_bill: data.is_gst_bill ?? true,
      originalSubtotal,
      originalDiscountAmount,
      originalRoundOff: Math.abs(originalRoundOff),
      isPositiveRoundOff,
      calculatedGrandTotal,
      shouldBeInGrandTotalMode
    };
  }

  const fetchData = useCallback(
    async (invoiceNumber) => {
      setLoading(true);
      try {
        const response = await fetchInvoiceByInvoiceNumber(invoiceNumber);
        const invoiceData = Array.isArray(response.results)
          ? response.results[0]
          : response;
        const mappedData = mapInvoiceToFormik(invoiceData);
        const loadedBillingProvider = getInvoiceBillingProviderValue(invoiceData);
        setInitialValues(mappedData);
        setOriginalInvoiceData(invoiceData);
        setCustomerInfo(mappedData.customerInfo);
        setBillingProvider(loadedBillingProvider);
        setOriginalBillingProvider(loadedBillingProvider);
        
        if (mappedData.shouldBeInGrandTotalMode) {
          console.log('Setting grand total mode with:', mappedData.calculatedGrandTotal);
          setIsGrandTotalMode(true);
          setGrandTotalInput(mappedData.calculatedGrandTotal);
        }
      } catch (err) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const hasSufficientStock = (medicineItems) => {
    for (let item of medicineItems) {
      const batchAllocation = item?.batch_allocations;
      const allocatedQuantity = (batchAllocation?.allocations || []).reduce(
        (total, allocation) => total + Number(allocation.quantity || 0),
        0
      );
      if (
        !batchAllocation?.allocations?.length ||
        batchAllocation?.hasInsufficientStock ||
        allocatedQuantity !== Number(item.quantity)
      ) {
        return false;
      }
    }
    return true;
  }

  const getPriceForSaleItem = (allocation, itemData) => {
    const sellingPrice = allocation.sellingPrice ?? itemData?.selling_price ?? 0;
    const buyingPrice = allocation.unitCost ?? itemData?.buying_price ?? 0;
    return parseFloat(salePriceType === 'selling_price' ? sellingPrice : buyingPrice);
  }
  
  const getItemsUsingAllocations = (medicineItems) => {
    // Might change 
    const items = [];
    for (let item of medicineItems) {
      for (let allocation of item?.batch_allocations?.allocations || []) {
        // Find matching invoice detail to get GST percentage
        // Note: the original item has discount_in_perc directly
        const unitPrice = getPriceForSaleItem(allocation, item);
        
        // Use calculateInclusiveLineGst to scale GST per allocation quantity
        const gstResult = calculateInclusiveLineGst({
          grossUnitPrice: Number(unitPrice),
          quantity: allocation.quantity,
          discountPercentage: Number(item.discount_in_perc || 0),
          taxPercentage: Number(item.gst_percentage || 0),
          gstEnabled: true,
          mode: 'cgst_sgst'
        });

        items.push({
          ...item,
          expiry_date: allocation.expiryDate || '',
          batch_number: allocation.batchNumber || '',
          manufacturing_date: allocation.manufacturingDate || '',
          quantity: allocation.quantity,
          price: unitPrice * allocation.quantity,
          cgst: gstResult.cgstAmount,
          sgst: gstResult.sgstAmount,
          igst: gstResult.igstAmount,
        });
      }
    }
    return items;
  }

  const handleCancellation = async (values) => {
    // Allow admin, pharmacy_biller, and account_manager to cancel invoices
    const canCancel = isAdmin || roles.includes('pharmacy_biller') || roles.includes('account_manager');
    if (!canCancel) {
      openSnackbar({
        open: true,
        message: 'Only Pharmacy Billers and Account Managers can cancel pharmacy invoices.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    // Validate cancellation_type is selected
    if (!values.cancellation_type) {
      openSnackbar({
        open: true,
        message: 'Please select cancellation type (Sale Return or Cancellation).',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    const selectedStore = JSON.parse(window.localStorage.getItem('selectedStore'))?.id || values.store_id;

    if (!selectedStore) {
      openSnackbar({
        open: true,
        message: 'Store information not found.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    // Check if discount has changed from initial value
    const discountChanged = Number(values.discount || 0) !== Number(initialValues.discount || 0);
    const itemsToCancel = values.invoice_detail
      .filter(item => item.cancel_quantity > 0 && (item.medicineId || item.nonMedicineId))
      .map(item => ({
        id: item.medicineId || item.nonMedicineId,
        cancel_quantity: Number(item.cancel_quantity),
        discount_in_perc: Number(item.discount_in_perc || 0)
      }));
    
    if (itemsToCancel.length === 0 && !discountChanged) {
      openSnackbar({
        open: true,
        message: 'Please specify quantities to cancel or modify the discount.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    // If discount changed, send all items with updated discount
    let pharmacyItems = itemsToCancel;
    if (discountChanged) {
      // When discount changes, we need to send all items with the updated discount
      // Merge cancelled items with all other items that need discount update
      const allItemsWithUpdatedDiscount = values.invoice_detail
        .filter(item => (item.medicineId || item.nonMedicineId))
        .map(item => {
          const existingCancellation = itemsToCancel.find(cancelItem => 
            cancelItem.id === (item.medicineId || item.nonMedicineId)
          );
          
          return {
            id: item.medicineId || item.nonMedicineId,
            cancel_quantity: existingCancellation ? existingCancellation.cancel_quantity : 0,
            discount_in_perc: Number(item.discount_in_perc || 0)
          };
        });
      
      pharmacyItems = allItemsWithUpdatedDiscount;
    }

    const payload = {
      store_id: selectedStore,
      cancellation: {
        pharmacy: pharmacyItems
      },
      cancellation_type: values.cancellation_type,
      ...(values.cancellation_notes ? { notes: values.cancellation_notes } : {})
    };

    try {
      // Always try the cancellation first - let backend determine if OTP is required
      const result = await cancelInvoiceItems(originalInvoiceData.invoice_number, payload);

      if (result?.requiresCancellationOtp && result?.message_id) {
        setPendingOldBillPayload(payload);
        setOldBillOtpMessageId(result.message_id);
        setOldBillOtpIsBulk(false);
        setOldBillBulkContext(null);
        setOldBillOtpModalOpen(true);
        return;
      }

      // Check if it's an OTP response for >50% discount (direct owner approval) - SUCCESS SCENARIO
      if (result?.message_id && result?.message) {
        setPendingCancellationPayload(payload);
        setCancellationOtpToken(result.message_id);
        setCancellationOtpMessageId(result.message_id);
        setCancellationShowDoctorSelection(false);
        setCancellationIsOTPSent(true);
        setCancellationOtpModalOpen(true);
        return;
      }
      
      // If we get here, no OTP was required - proceed with success
      openSnackbar({
        open: true,
        message: 'Items cancelled successfully!',
        variant: 'alert',
        alert: { color: 'success' },
      });

      await completeCancellation(result, itemsToCancel);
    } catch (err) {
      const errorMessage = extractErrorMessage(err) || 'Failed to cancel items';
      
      // Check if it's the specific error for 25-50% discount requiring doctor selection
      if (errorMessage === ERROR_MESSAGES.INVALID_APPROVER) {
        // Store the payload for later use
        setPendingCancellationPayload(payload);
        setCancellationShowDoctorSelection(true);
        setCancellationOtpModalOpen(true);
        return;
      }
      
      // Check if it's an OTP response for >50% discount (direct owner approval)
      if (err.response?.data?.message_id && err.response?.data?.message) {
        setPendingCancellationPayload(payload);
        setCancellationOtpToken(err.response.data.message_id);
        setCancellationOtpMessageId(err.response.data.message_id);
        setCancellationShowDoctorSelection(false);
        setCancellationIsOTPSent(true);
        setCancellationOtpModalOpen(true);
        return;
      }
      
      // Handle other errors
      openSnackbar({
        open: true,
        message: errorMessage,
        variant: 'alert',
        alert: { color: 'error' },
      });
    }
  };

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    // Don't allow form submission in cancellation mode
    if (isCancellationMode) {
      setSubmitting(false);
      openSnackbar({
        open: true,
        message: 'Use cancellation button to cancel items.',
        variant: 'alert',
        alert: { color: 'info' }
      });
      return;
    }

    const selectedStoreObject = getSelectedStoreObject();
    const selectedStore = selectedStoreObject?.id

    if (!selectedStore) {
      openSnackbar({
        open: true,
        message: `Please select a store before creating a ${variant === 'pharmacy' ? 'pharmacy' : 'sale'} order.`,
        variant: 'alert',
        alert: { color: 'error' }
      });
      setSubmitting(false);
      return;
    }

    if (variant === 'sale' && !values.supplier_id) {
      openSnackbar({
        open: true,
        message: 'Please select a wholesaler before creating a sale order.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      setSubmitting(false);
      return;
    }
    if (variant === 'pharmacy' && !values.customerInfo?.id) {
      openSnackbar({
        open: true,
        message: 'Please select a patient before creating a pharmacy order.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      setSubmitting(false);
      return;
    }

    const hasTest = Array.isArray(values.invoice_detail) &&
      values.invoice_detail.some(item => item.medicineId || item.nonMedicineId);
    if (!hasTest) {
      openSnackbar({
        open: true,
        message: 'Please add at least one medicine .',
        variant: 'alert',
        alert: { color: 'error' }
      });
      setSubmitting(false);
      return;
    }
    setSubmitting(true);
    
    if (!values.payment_method && variant !== 'pharmacy_bulk_cancel') {
      openSnackbar({
        open: true,
        message: 'Please select at least one payment method',
        variant: 'alert',
        alert: { color: 'error' }
      });
      setSubmitting(false);
      return;
    }

    if (values.payment_method === 'hybrid') {
      const cash = Number(values.cash_amount);
      const online = Number(values.online_amount);
      if (!(cash > 0 && online > 0)) {
        openSnackbar({
          open: true,
          message: 'For hybrid payment, both cash and online amounts must be greater than 0',
          variant: 'alert',
          alert: { color: 'error' }
        });
        setSubmitting(false);
        return;
      }
    }

    const getItemSubtotalForSubmit = (item) => {
      if (variant === 'sale' && item.batch_allocations?.allocations) {
        if (salePriceType === 'buying_price') {
          return item.batch_allocations.allocations.reduce((total, allocation) => 
            total + parseFloat(allocation.unitCost || item.buying_price || 0) * allocation.quantity, 0);
        } else {
          return item.batch_allocations.allocations.reduce((total, allocation) => 
            total + parseFloat(allocation.sellingPrice ?? item.selling_price ?? 0) * allocation.quantity, 0);
        }
      }
      return Number(item.selling_price || 0) * Number(item.qty || 1);
    };

    const subtotal = values.invoice_detail.reduce((sum, item) => {
      return item.medicineId || item.nonMedicineId ? sum + getItemSubtotalForSubmit(item) : sum;
    }, 0);
    const isGstBillSelected = values.is_gst_bill ?? true;
    const sellMedicinesPayload = values.invoice_detail
      .filter(item => item.medicineId || item.nonMedicineId)
      .map(item => {
        const gstResult = calculateInclusiveLineGst({
          grossUnitPrice: Number(item.selling_price || 0),
          quantity: item.qty,
          discountPercentage: Number(item.discount_in_perc || 0),
          taxPercentage: isGstBillSelected ? Number(item.gst_percentage || 0) : 0,
          gstEnabled: isGstBillSelected,
          mode: 'cgst_sgst'
        });

        return {
          ...(item.nonMedicineId
            ? { non_medicine_item_id: item.nonMedicineId }
            : { medicine_id: item.medicineId }),
          quantity: item.qty,
          cancel_quantity: item.cancel_quantity || 0,
          buying_price: Number(item.buying_price || 0),
          unit_price: Number(item.selling_price || 0),
          batch_allocations: item.batch_allocations || {},
          batches: (item.batch_allocations?.allocations || []).map((allocation) => ({
            batch_number: allocation.batchNumber,
            quantity: Number(allocation.quantity)
          })),
          name: item.name,
          // Always use item discount (main discount applies to items when edited)
          discount_in_perc: Number(item.discount_in_perc || 0),
          gst_percentage: isGstBillSelected ? Number(item.gst_percentage || 0) : 0,
          cgst: gstResult.cgstAmount,
          sgst: gstResult.sgstAmount,
          igst: gstResult.igstAmount,
        };
      });
    
    let actualRoundOff = 0;
    if (isGrandTotalMode && grandTotalInput !== null && grandTotalInput > 0) {
      const roundOffData = calculateRoundOffAmount(subtotal, grandTotalInput);
      actualRoundOff = roundOffData.amount;
      actualRoundOff = roundOffData.isPositive ? actualRoundOff : -actualRoundOff;
    } else {
      const gstSummaryForSubmit = summarizeInclusiveGst({
        items: sellMedicinesPayload.map((item) => ({
          ...item,
          quantity: item.quantity,
          selling_price: item.unit_price ?? item.selling_price
        })),
        gstEnabled: isGstBillSelected,
        mode: 'cgst_sgst'
      });
      actualRoundOff = calculatePharmacyInvoiceRounding(gstSummaryForSubmit.totalAmount).roundOff;
    }
    const itemGstPercentages = values.invoice_detail
      .filter(item => item.medicineId || item.nonMedicineId)
      .map(item => isGstBillSelected ? Number(item.gst_percentage || 0) : 0);
    const additionalInfo = {};
    if (values.doctor_id) {
      additionalInfo.doctor_id = values.doctor_id;
      // Get doctor name from doctors array
      const selectedDoctor = doctors.find(doc => doc.id === values.doctor_id);
      if (selectedDoctor) {
        additionalInfo.doctor_name = selectedDoctor.full_name;
      }
    }

    let payload = {
      items: sellMedicinesPayload,
      customer_id: values.customerInfo?.id || '',
      payment_method: values.payment_method,
      notes: values.internal_notes,
      store_id: selectedStore,
      round_off: actualRoundOff,
      is_gst_bill: values.is_gst_bill ?? true,
      billing_provider: billingProvider || 'self',
      ...(Object.keys(additionalInfo).length > 0 && { other_details: JSON.stringify(additionalInfo) })
    };

    if (variant === 'pharmacy') {
      if (!isInPlaceEditMode && !hasSufficientStock(sellMedicinesPayload)) {
        setSubmitting(false);
        openSnackbar({
          open: true,
          message: 'Allocate the full quantity from available batches before creating the invoice.',
          variant: 'alert',
          alert: { color: 'error' }
        });
        return;
      }

      payload = {
        items: sellMedicinesPayload,
        customer_id: values.customerInfo?.id || '',
        payment_method: values.payment_method,
        notes: values.internal_notes,
        store_id: selectedStore,
        round_off: actualRoundOff,
        is_gst_bill: values.is_gst_bill ?? true,
        billing_provider: billingProvider || 'self',
        ...(Object.keys(additionalInfo).length > 0 && { other_details: JSON.stringify(additionalInfo) })
      };
      if (values.payment_method === 'hybrid') {
        payload.cash_amount = String(values.cash_amount);
        payload.online_amount = String(values.online_amount);
      }
      
      try {
        if (isInPlaceEditMode && invoiceId) {
            const editPayloadItems = [];
            sellMedicinesPayload.forEach(item => {
                const originalQty = item.quantity;
                const cancelQty = item.cancel_quantity || 0;
                
                // 1. Active Item (always send original quantity)
                editPayloadItems.push({
                    id: item.medicine_id || item.non_medicine_item_id,
                    type: item.medicine_id ? 'medicine' : 'non_medicine_item',
                    name: item.name,
                    discount_in_perc: item.discount_in_perc,
                    quantity: originalQty,
                    buying_price: item.buying_price,
                    selling_price: item.unit_price,
                    status: 'active',
                    gst_percentage: item.gst_percentage,
                    tax_in_perc: item.gst_percentage,
                    gst_rate: item.gst_percentage,
                    cgst: item.cgst,
                    sgst: item.sgst,
                    igst: item.igst,
                    batches: item.batch_allocations?.allocations?.map(b => ({
                        batch_number: b.batchNumber,
                        quantity: b.quantity,
                        expiry_date: b.expiryDate,
                        buying_price: b.unitCost,
                        selling_price: item.unit_price !== undefined ? item.unit_price : b.sellingPrice
                    })) || []
                });

                // 2. Cancelled Item (if any)
                if (cancelQty > 0) {
                    let remainingToCancel = cancelQty;
                    const cancelBatches = [];
                    
                    for (const b of (item.batch_allocations?.allocations || [])) {
                        if (remainingToCancel >= b.quantity) {
                            cancelBatches.push({
                                batch_number: b.batchNumber,
                                quantity: b.quantity,
                                expiry_date: b.expiryDate,
                                buying_price: b.unitCost,
                                selling_price: item.unit_price !== undefined ? item.unit_price : b.sellingPrice
                            });
                            remainingToCancel -= b.quantity;
                        } else if (remainingToCancel > 0) {
                            cancelBatches.push({
                                batch_number: b.batchNumber,
                                quantity: remainingToCancel,
                                expiry_date: b.expiryDate,
                                buying_price: b.unitCost,
                                selling_price: item.unit_price !== undefined ? item.unit_price : b.sellingPrice
                            });
                            remainingToCancel = 0;
                        }
                    }

                    const cancelGstResult = calculateInclusiveLineGst({
                        grossUnitPrice: Number(item.unit_price || 0),
                        quantity: cancelQty,
                        discountPercentage: Number(item.discount_in_perc || 0),
                        taxPercentage: isGstBillSelected ? Number(item.gst_percentage || 0) : 0,
                        gstEnabled: isGstBillSelected,
                        mode: 'cgst_sgst'
                    });

                    editPayloadItems.push({
                        id: item.medicine_id || item.non_medicine_item_id,
                        type: item.medicine_id ? 'medicine' : 'non_medicine_item',
                        name: item.name,
                        discount_in_perc: item.discount_in_perc,
                        quantity: cancelQty,
                        buying_price: item.buying_price,
                        selling_price: item.unit_price,
                        status: 'cancelled',
                        gst_percentage: item.gst_percentage,
                        tax_in_perc: item.gst_percentage,
                        gst_rate: item.gst_percentage,
                        cgst: cancelGstResult.cgstAmount,
                        sgst: cancelGstResult.sgstAmount,
                        igst: cancelGstResult.igstAmount,
                        batches: cancelBatches
                    });
                }
            });

            const editPayload = {
                category: 'pharmacy',
                items: editPayloadItems,
                store_id: values.store_id || selectedStore,
                notes: values.internal_notes || '',
              payment_method: values.payment_method,
              cash_amount: values.payment_method === 'hybrid' ? String(values.cash_amount) : null,
              online_amount: values.payment_method === 'hybrid' ? String(values.online_amount) : null,
                ...(values.customerInfo?.id && { customer_id: values.customerInfo.id }),
                ...(billingProvider !== originalBillingProvider && {
                  billing_provider: billingProvider || 'self'
                }),
                ...(Object.keys(additionalInfo).length > 0 && { other_details: additionalInfo })
            };
            await editInvoice(invoiceId, editPayload);
            openSnackbar({
                open: true,
                message: 'Invoice successfully edited in-place!',
                variant: 'alert',
                alert: { color: 'success' },
            });
            navigate('/invoice/list?activeTab=pharmacy');
            return;
        }

        const result = await sellMedicines(payload);
        
        if (result?.message_id && result?.message) {
          setPendingPayload(payload);
          setCustomerInfo(values?.customerInfo);
          setOtpToken(result.message_id);
          setOtpMessageId(result.message_id);
          setShowDoctorSelection(false);
          setIsOTPSent(true);
          setOtpModalOpen(true);
          setSubmitting(false);
          return;
        }
        
        const responseStoreObject = result?.store || selectedStoreObject;
        const responseHospital = normalizeHospitalDetails(responseStoreObject);
        const pharmacyItems = (result?.items?.pharmacy || []).map((item, index) => ({
          ...item,
          gst_percentage: isGstBillSelected ? (getItemGstPercentage(item) || itemGstPercentages[index] || 0) : 0
        }));
        const activeItems = pharmacyItems.filter(item => !item.status || item.status === 'active');
        
        const subtotal = activeItems.reduce((sum, item) => sum + (Number(item.selling_price || 0) * Number(item.quantity || 1)), 0);
        
        const totalDiscount = activeItems.reduce((sum, item) =>
          sum + ((Number(item.selling_price || 0) * Number(item.quantity || 1)) * Number(item.discount_in_perc || 0)) / 100, 0);
        
        const pharmacyGstSummary = summarizeInclusiveGst({
          items: activeItems,
          gstEnabled: isGstBillSelected,
          mode: 'cgst_sgst',
          summarySource: result
        });
        const resolvedTotals = resolvePharmacyInvoiceTotals({
          netTotal: pharmacyGstSummary.totalAmount
        });

        const responseCustomer =
          result?.customer && typeof result.customer === 'object'
            ? result.customer
            : values?.customerInfo;
        const enhancedResponse = {
          ...result,
          payment_method: result?.payment_method || values.payment_method,
          customer: responseCustomer,
          items: {
            ...result?.items,
            pharmacy: pharmacyItems
          },
          actualSubtotal: subtotal,
          actualDiscount: totalDiscount,
          roundOff: resolvedTotals.roundOffDisplay,
          isPositiveRoundOff: resolvedTotals.isPositiveRoundOff,
          finalTotal: resolvedTotals.grandTotal,
          total_amount: resolvedTotals.grandTotal,
          round_off: resolvedTotals.roundOff,
          store: responseStoreObject,
          hospital_details: responseHospital,
          pharmacy_gst_summary: pharmacyGstSummary
        };
        setMedicineOrderResponse(enhancedResponse);
        setCustomerInfo(values?.customerInfo);
        setAutoSendWhatsAppKey(`pharmacy-invoice:${enhancedResponse.invoice_number || enhancedResponse.id}:${Date.now()}`);
        openSnackbar({
          open: true,
          message: 'Pharmacy order created successfully!',
          variant: 'alert',
          alert: { color: 'success' },
        });
        setOpenTokenModal(true);

      } catch (err) {
        const errorMessage = extractErrorMessage(err) || 'Failed to create pharmacy order';
        
        // Check if it's the specific error for 25-50% discount requiring doctor selection
        if (errorMessage === ERROR_MESSAGES.INVALID_APPROVER) {
          // Store the payload for later use
          setPendingPayload(payload);
          setCustomerInfo(values?.customerInfo);
          setShowDoctorSelection(true);
          setOtpModalOpen(true);
          setSubmitting(false);
          return;
        }
        
        // Check if it's an OTP response for >50% discount (direct owner approval)
        if (err.response?.data?.message_id && err.response?.data?.message) {
          setPendingPayload(payload);
          setCustomerInfo(values?.customerInfo);
          setOtpToken(err.response.data.message_id);
          setOtpMessageId(err.response.data.message_id);
          setShowDoctorSelection(false);
          setIsOTPSent(true);
          setOtpModalOpen(true);
          setSubmitting(false);
          return;
        }
        
        // Handle other errors
        openSnackbar({
          open: true,
          message: errorMessage,
          variant: 'alert',
          alert: { color: 'error' },
        });
      } finally {
        setSubmitting(false);
      }
    } else if (variant === 'sale') {
      if (!hasSufficientStock(sellMedicinesPayload)) {
        setSubmitting(false);
        openSnackbar({
          open: true,
          message: 'Not enough stock for an item',
          variant: 'alert',
          alert: { color: 'error' }
        });
        return;
      }

      payload = {
        store_id: selectedStore,
        supplier_id: values.supplier_id || '',
        payment_method: values.payment_method,
        price_type: salePriceType,
        items: getItemsUsingAllocations(sellMedicinesPayload),
        notes: values.internal_notes,
        round_off: actualRoundOff,
        is_gst_bill: values.is_gst_bill ?? true,
      };

      try{
        const result = await sellMedicineToSupplier(payload);
        const enhancedResponse = {
          ...result,
          wholesaler_supplier_name: selectedWholesaler?.name || 'N/A',
          store: JSON.parse(window.localStorage.getItem('selectedStore')) || {},
          created_at: new Date(),
          payment_method: values.payment_method,
          price_type: salePriceType
        };
        
        openSnackbar({
          open: true,
          message: 'Sale order created successfully!',
          variant: 'alert',
          alert: { color: 'success' },
        });
        setSaleBillItem(enhancedResponse);
        setSaleBillPrintModal(true);
      } catch (err) {
        const errorMessage = extractErrorMessage(err) || 'Failed to create sale order';
        openSnackbar({
          open: true,
          message: errorMessage,
          variant: 'alert',
          alert: { color: 'error' },
        });
      } finally {
        setSubmitting(false);
      }
    } else if (variant === 'pharmacy_bulk_cancel') {
      // Validate cancellation_type is selected
      if (!values.cancellation_type) {
        openSnackbar({
          open: true,
          message: 'Please select cancellation type (Sale Return or Cancellation).',
          variant: 'alert',
          alert: { color: 'error' }
        });
        setSubmitting(false);
        return;
      }

      setSubmitting(true);
      payload = {
        customer_id: values.customerInfo?.id || '',
        store_id: selectedStore,
        cancellation: {
          pharmacy: sellMedicinesPayload.map(item => {
            return {
              name: item.name,
              id: item.medicine_id || item.non_medicine_item_id,
              cancel_quantity: item.quantity,
            }
          }),
        },
        cancellation_type: values.cancellation_type,
        ...(values.cancellation_notes ? { notes: values.cancellation_notes } : {})
      };
      try {
        const result = await bulkCancelInvoiceItems(payload);

        if (result?.requiresCancellationOtp && result?.message_id) {
          setPendingOldBillPayload(payload);
          setOldBillOtpMessageId(result.message_id);
          setOldBillOtpIsBulk(true);
          setOldBillBulkContext({
            items: sellMedicinesPayload.map(item => ({
              id: item.medicine_id || item.non_medicine_item_id,
              name: item.name,
              quantity: item.quantity
            })),
            customerInfo: values.customerInfo
          });
          setOldBillOtpModalOpen(true);
          setSubmitting(false);
          return;
        }

        const cancelledResult = result?.cancelled;
        const totalAmount = cancelledResult[cancelledResult.length - 1]?.total_amount_cancelled || 0;
        
        // Create maps to sum cancelled_item_amount and cancelled_qty from the API response
        // Filter out the last item which contains total_amount_cancelled
        // Sum amounts and quantities for items with the same item_id (same medicine in multiple invoices)
        const cancelledItemsAmountMap = {};
        const cancelledItemsQtyMap = {};
        const invoiceIdsSet = new Set();
        
        cancelledResult.slice(0, -1).forEach(cancelledItem => {
          if (cancelledItem.item_id) {
            const itemId = String(cancelledItem.item_id);
            // Sum up amounts for the same item_id
            if (cancelledItem.cancelled_item_amount !== undefined) {
              cancelledItemsAmountMap[itemId] = (cancelledItemsAmountMap[itemId] || 0) + Number(cancelledItem.cancelled_item_amount || 0);
            }
            // Sum up quantities for the same item_id
            if (cancelledItem.cancelled_qty !== undefined) {
              cancelledItemsQtyMap[itemId] = (cancelledItemsQtyMap[itemId] || 0) + Number(cancelledItem.cancelled_qty || 0);
            }
          }
          // Collect unique invoice IDs
          if (cancelledItem.invoice_id) {
            invoiceIdsSet.add(cancelledItem.invoice_id);
          }
        });
        
        const invoiceIds = Array.from(invoiceIdsSet).sort();
        
        const enhancedResponse = {
          invoiceData: {
            items: sellMedicinesPayload.map(item => {
              const itemId = String(item.medicine_id || item.non_medicine_item_id);
              // Get the sum of cancelled amounts and quantities from API response
              const amount = cancelledItemsAmountMap[itemId] || 0;
              const quantity = cancelledItemsQtyMap[itemId] || item.quantity;
              
              return {
                id: item.medicine_id || item.non_medicine_item_id,
                name: item.name,
                quantity: quantity,
                amount: amount
              }
            }),
            store: JSON.parse(window.localStorage.getItem('selectedStore')) || {},
            created_at: new Date(),
            totalAmount,
            cancelledInvoiceIds: invoiceIds
          },
          customerInfo: values.customerInfo,
        };
        openSnackbar({
          open: true,
          message: 'Invoice cancelled successfully!',
          variant: 'alert',
          alert: { color: 'success' },
        });
        setBulkCancelItem(enhancedResponse);
        setBulkCancelPrintModal(true);
      } catch (err) {
        let errorMessage;
        if (err.response?.data?.unfulfilled?.length > 0) {
          errorMessage = generateBulkCancelUnfulfilledError(sellMedicinesPayload, err.response.data.unfulfilled);
        } else {
          errorMessage = extractErrorMessage(err) || 'Failed to cancel lab tests';
        }
        openSnackbar({
          open: true,
          message: errorMessage,
          variant: 'alert',
          alert: { color: 'error' },
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const fetchAllItemsDirect = async (search) => {
    try {
      return await fetchAllItems(search);
    } catch (error) {
      console.error('Error fetching items:', error);
      return [];
    }
  };

  useEffect(() => {
    if (invoiceId) {
      fetchData(invoiceId);
    } else {
      // Reset OTP states when starting a new invoice
      resetOTPStates();
    }
  }, [invoiceId, fetchData]);

  // Reset OTP states when component mounts
  useEffect(() => {
    resetOTPStates();
  }, []);

  // Fetch doctors list
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setIsLoadingDoctors(true);
        const data = await fetchDoctors();
        setDoctors(data);
      } catch (error) {
        console.error('Error fetching doctors:', error);
        openSnackbar({
          open: true,
          message: 'Failed to load doctors list',
          variant: 'alert',
          alert: { color: 'error' }
        });
      } finally {
        setIsLoadingDoctors(false);
      }
    };
    loadDoctors();
  }, []);

  // Refresh hospital GST details from BE and merge into localStorage store object
  useEffect(() => {
    const refreshHospitalDetails = async () => {
      try {
        const storeObj = JSON.parse(window.localStorage.getItem('selectedStore') || 'null');
        if (!storeObj) return;
        const details = await fetchHospitalDetails();
        if (!details) return;
        const updated = { ...storeObj, ...details };
        window.localStorage.setItem('selectedStore', JSON.stringify(updated));
      } catch (error) {
        // Non-critical — fall back to whatever is in localStorage
      }
    };
    refreshHospitalDetails();
  }, []);

  // Auto-fill doctor from logged-in user when doctors list is ready (new invoice only)
  useEffect(() => {
    if (invoiceId || !doctors?.length || !user?.id) return;
    const matchedDoctor = doctors.find((d) => d.id === user.id);
    if (matchedDoctor) {
      setInitialValues((prev) => (prev.doctor_id ? prev : { ...prev, doctor_id: matchedDoctor.id }));
    }
  }, [doctors, user?.id, invoiceId]);

  const getSubtotalOfSaleItem = (itemData) => {
    const batchAllocations = itemData?.batch_allocations;
    if (!batchAllocations || batchAllocations?.hasInsufficientStock) {
      return 0;
    }
    if (salePriceType === "selling_price") {
      return batchAllocations?.allocations?.reduce((total, allocation) => 
        total + parseFloat(allocation.sellingPrice ?? itemData?.selling_price ?? 0) * allocation.quantity,
        0);
    } else {
      return batchAllocations?.allocations?.reduce((total, allocation) => 
        total + parseFloat(allocation.unitCost || itemData?.buying_price || 0) * allocation.quantity,
        0);
    }
  }   
  const hospitalData = useHospitalDetails();

  const isThermalPrint = true;
  return (
    <>
    {(variant === 'pharmacy') &&
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="error" onClick={() => navigate('/invoice/bulk-cancel-pharmacy-invoice')}>
          Bulk Cancel
        </Button>
      </Box>
    }
    {(variant === 'pharmacy_bulk_cancel') &&
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => navigate('/invoice/pharmacy-invoice')}>
          New Invoice
        </Button>
      </Box>
    }
    <MainCard>
      {isLoader ? (
        <Box sx={{ height: 'calc(100vh - 310px)' }}><CircularLoader /></Box>
      ) : (
        <Formik 
          key={JSON.stringify(initialValues)} 
          initialValues={initialValues} 
          enableReinitialize={true} 
          validate={(vals) => {
            const err = {};
            if (!vals.payment_method && variant !== 'pharmacy_bulk_cancel') {
              err.payment_method = 'Please select at least one payment method';
            }
            if (vals.payment_method === 'hybrid') {
              const cash = Number(vals.cash_amount);
              const online = Number(vals.online_amount);
              if (!(cash > 0)) err.cash_amount = 'Must be greater than 0';
              if (!(online > 0)) err.online_amount = 'Must be greater than 0';
            }
            return err;
          }}
          onSubmit={handleFormSubmit}
        >
          {(formikProps) => {
            const { values, setFieldValue } = formikProps;
            const selectedHospitalDetails = normalizeHospitalDetails(getSelectedStoreObject());
            const showPharmacyGst = variant === 'pharmacy' || variant === 'sale';
            
            let subtotal = 0  
            if (isSaleBill) {
              subtotal = values.invoice_detail.reduce((sum, item) => {
                if (item.name.trim().length === 0) return sum;
                const baseQty = Number(item.qty || 0);
                const cancelledQty = Number(item.cancel_quantity || 0);
                const effectiveQty = isCancellationMode ? Math.max(baseQty - cancelledQty, 0) : baseQty;
                return sum + getSubtotalOfSaleItem({ ...item, qty: effectiveQty });
              }, 0);
            } else {
              subtotal = values.invoice_detail.reduce((sum, item) => {
                // Exclude cancelled items from subtotal calculation
                const isCancelled = item.isCancelled || item.qty <= 0;
                if (item.name.trim().length === 0 || isCancelled) return sum;
                const baseQty = Number(item.qty || 0);
                const cancelledQty = Number(item.cancel_quantity || 0);
                const effectiveQty = isCancellationMode ? Math.max(baseQty - cancelledQty, 0) : baseQty;
                return sum + item.selling_price * effectiveQty;
              }, 0);            
            }

            const taxRate = 0;
            
            const totalDiscount = values.invoice_detail.reduce((sum, item) => {
              const isCancelled = item.isCancelled || item.qty <= 0;
              if (item.name.trim().length > 0 && !isCancelled) {
                const baseQty = Number(item.qty || 0);
                const cancelledQty = Number(item.cancel_quantity || 0);
                const effectiveQty = isCancellationMode ? Math.max(baseQty - cancelledQty, 0) : baseQty;
                
                const itemSubtotal = isSaleBill ? getSubtotalOfSaleItem({ ...item, qty: effectiveQty }) : item.selling_price * effectiveQty;
                const itemDiscount = Number(item.discount_in_perc || 0);
                return sum + (itemSubtotal * itemDiscount / 100);
              }
              return sum;
            }, 0);
            
            // Calculate average discount percentage from items (weighted by subtotal)
            const calculatedMainDiscount = subtotal > 0 ? (totalDiscount / subtotal) * 100 : 0;
            
            // Sync main discount field with calculated average when individual discounts change
            // Only update if main discount is not currently being edited
            if (!isMainDiscountEditingRef.current && subtotal > 0) {
              const calculatedValue = parseFloat(calculatedMainDiscount.toFixed(2));
              const currentValue = parseFloat(Number(values.discount || 0).toFixed(2));
              // Only update if the calculated value differs significantly (more than 0.01)
              // and if it's different from what we last synced
              if (Math.abs(calculatedValue - currentValue) > 0.01 && lastSyncedDiscountRef.current !== calculatedValue) {
                setFieldValue('discount', calculatedValue.toFixed(2));
                lastSyncedDiscountRef.current = calculatedValue;
              }
            }
            
            // Use totalDiscount (calculated from individual items) for display
            let pharmacyGstSummary = null;
            if (showPharmacyGst) {
              const itemsForGst = values.invoice_detail
                .map((item) => {
                  const baseQty = Number(item.qty || 0);
                  const cancelledQty = Number(item.cancel_quantity || 0);
                  const effectiveQty = isCancellationMode ? Math.max(baseQty - cancelledQty, 0) : baseQty;
                  return {
                    ...item,
                    selling_price: Number(item.selling_price ?? item.price ?? 0),
                    quantity: effectiveQty,
                    batches: [],
                    cgst_amount: 0,
                    sgst_amount: 0,
                    igst_amount: 0,
                    cgst: 0,
                    sgst: 0,
                    igst: 0
                  };
                })
                .filter((item) =>
                  item.name?.trim()?.length > 0 &&
                  !item.isCancelled &&
                  item.status !== 'cancelled' &&
                  Number(item.quantity || 0) > 0
                );

              if (variant === 'sale') {
                const normSupplier = normalizeSupplierDetails(selectedWholesaler || {});
                pharmacyGstSummary = summarizePurchaseBillGst({
                  items: itemsForGst.map(item => ({
                    ...item,
                    buying_price: item.selling_price,
                    discount_in_perc: item.discount_in_perc,
                    tax_in_perc: item.gst_percentage
                  })),
                  supplierGstNumber: normSupplier.gstNumber,
                  hospitalGstNumber: selectedHospitalDetails.gstNumber,
                  gstEnabled: values.is_gst_bill ?? true,
                });
              } else {
                pharmacyGstSummary = summarizeInclusiveGst({
                  items: itemsForGst,
                  gstEnabled: values.is_gst_bill ?? true,
                  mode: 'cgst_sgst'
                });
              }
            }

            let discountRate, total, actualRoundOff, isPositiveRoundOff;
            if (isGrandTotalMode && grandTotalInput !== null && grandTotalInput > 0) {
              const calculatedDiscount = calculateDiscountFromTotal(subtotal, grandTotalInput);
              discountRate = (calculatedDiscount * subtotal) / 100;
              total = grandTotalInput;
              const roundOffData = calculateRoundOffAmount(subtotal, grandTotalInput);
              actualRoundOff = roundOffData.amount;
              isPositiveRoundOff = roundOffData.isPositive;
            } else {
              discountRate = totalDiscount;
              const netForRound =
                pharmacyGstSummary?.totalAmount ?? parseFloat((subtotal - discountRate).toFixed(2));
              const rounding = calculatePharmacyInvoiceRounding(netForRound);
              total = rounding.grandTotal;
              actualRoundOff = rounding.roundOffDisplay;
              isPositiveRoundOff = rounding.isPositiveRoundOff;
            }
            
            const cancelledTotal = values.invoice_detail.reduce((sum, item) => {
              const price = Number(item.net_price || 0);
              const qty = item.cancel_quantity;
              return sum + price * qty;
            }, 0);

            const handleGrandTotalChange = (newTotal) => {
              setGrandTotalInput(newTotal);
              if (newTotal && newTotal > 0) {
                const newDiscount = calculateDiscountFromTotal(subtotal, newTotal);
                const discountValue = parseFloat(newDiscount.toFixed(2));
                setFieldValue('discount', discountValue.toFixed(2));
                
                // Update item discounts to match the new main discount (for round off sync)
                values.invoice_detail.forEach((_, index) => {
                  const item = values.invoice_detail[index];
                  // Only update items that are not cancelled and have a valid name
                  if (item.name && item.name.trim().length > 0 && !item.isCancelled && item.qty > 0) {
                    setFieldValue(`invoice_detail[${index}].discount_in_perc`, discountValue);
                  }
                });
              }
            };

            const handleDiscountChange = (newDiscount) => {
              setFieldValue('discount', newDiscount);
              setIsGrandTotalMode(false);
              setGrandTotalInput(null);
            };
            
            const handleMainDiscountFocus = () => {
              isMainDiscountEditingRef.current = true;
            };
            
            const handleMainDiscountBlur = () => {
              // Small delay to allow onChange to complete
              setTimeout(() => {
                isMainDiscountEditingRef.current = false;
                // Reset last synced value so it can sync again
                lastSyncedDiscountRef.current = null;
              }, 100);
            };

            const handlePatientSelected = async (patient) => {
              if (!patient?.id || isSaleBill || isCancellationMode) return;
              setCustomerInfo(patient);
              setBillingProvider(null);
              
              // Fetch user-specific discount
              try {
                const discountInfo = await fetchUserDiscountInfo('pharmacy', patient.id);
                setUserDiscount(discountInfo.discount);
                setHasUserDiscount(discountInfo.hasUserDiscount);
                
                if (discountInfo.hasUserDiscount) {
                  // Set the main discount field to user discount
                  setFieldValue('discount', discountInfo.discount);
                  
                  // Apply user discount to existing items that have no discount
                  // so items with explicit 0% discount will not get user discount applied
                  values.invoice_detail.forEach((item, index) => {
                    const hasItemDiscount = item.discount_in_perc !== null && item.discount_in_perc !== undefined && item.discount_in_perc !== '';
                    if ((item.medicineId || item.nonMedicineId) && !hasItemDiscount) {
                      setFieldValue(`invoice_detail[${index}].discount_in_perc`, discountInfo.discount);
                    }
                  });
                  
                  openSnackbar({
                    open: true,
                    message: `User-specific discount of ${discountInfo.discount}% available (applied to items without their own discount)`,
                    variant: 'alert',
                    alert: { color: 'success' }
                  });
                }
              } catch (error) {
                console.error('Error fetching user discount:', error);
              }
              
              try {
                const prescriptionData = await fetchLatestPrescriptionForPatient(patient.id, 7, true);

                if (prescriptionData.found && prescriptionData.medicines && prescriptionData.medicines.length > 0) {
                  const appointmentDate = format(parseISO(prescriptionData.appointment.created_at), 'dd/MM/yyyy');
                  const prescriptionDoctorId =
                    prescriptionData.appointment?.doctor_details?.id ??
                    prescriptionData.appointment?.doctor_id ??
                    prescriptionData.appointment?.doctor?.id ??
                    '';

                  if (prescriptionDoctorId !== null && prescriptionDoctorId !== undefined && prescriptionDoctorId !== '') {
                    const matchedPrescriptionDoctor = doctors.find((doc) => String(doc.id) === String(prescriptionDoctorId));
                    setFieldValue('doctor_id', matchedPrescriptionDoctor ? matchedPrescriptionDoctor.id : prescriptionDoctorId);
                  }
                  
                  const dbMedicinesForModal = [];
                  const customMedicinesForModal = [];
                  
                  for (const medicine of prescriptionData.medicines) {
                    const medicineName = medicine.medicine_name || '';
                    const searchName = medicineName.split('(')[0].trim();
                    
                    try {
                      const medicines = await fetchAllItemsDirect(searchName);
                      
                      if (medicines && medicines.length > 0) {
                        const matchedMedicine = medicines.find(m => {
                          const dbName = m.product_name?.toLowerCase().trim() || '';
                          const dbDisplayName = m.name?.toLowerCase().trim() || '';
                          const prescName = medicineName?.toLowerCase().trim() || '';
                          const prescSearchName = searchName?.toLowerCase().trim() || '';
                          
                          // Strict matching - exact match or starts with for product names
                          return dbName === prescSearchName ||
                                 dbName === prescName ||
                                 dbDisplayName === prescName ||
                                 dbDisplayName === prescSearchName ||
                                 // Only match if DB name starts with the search term (not contains)
                                 dbName.startsWith(prescSearchName) ||
                                 dbDisplayName.startsWith(prescSearchName);
                        });
                        
                        if (matchedMedicine) {
                          const displayName = matchedMedicine.type === "non-medical" 
                            ? matchedMedicine.name 
                            : `${matchedMedicine.product_name || matchedMedicine.brand_name} (${matchedMedicine.constituents_names?.join(' , ') || ''})`;
                          const hasPrescriptionDiscount =
                            medicine.discount !== null &&
                            medicine.discount !== undefined &&
                            medicine.discount !== '' &&
                            Number(medicine.discount) > 0;
                          const hasMedicineDiscount =
                            matchedMedicine.discount !== null &&
                            matchedMedicine.discount !== undefined &&
                            matchedMedicine.discount !== '';
                            
                          let resolvedDiscountRaw = null;
                          if (hasPrescriptionDiscount) resolvedDiscountRaw = medicine.discount;
                          else if (hasMedicineDiscount) resolvedDiscountRaw = matchedMedicine.discount;
                          
                          const resolvedDiscount =
                            resolvedDiscountRaw != null && Number.isFinite(Number(resolvedDiscountRaw))
                              ? Number(resolvedDiscountRaw)
                              : null;
                          
                          dbMedicinesForModal.push({
                            name: medicineName,
                            displayName: displayName,
                            medicineId: matchedMedicine.type !== "non-medical" ? matchedMedicine.id : undefined,
                            nonMedicineId: matchedMedicine.type === "non-medical" ? matchedMedicine.id : undefined,
                            type: matchedMedicine.type,
                            quantity: medicine.quantity || 1,
                            doseSchedules: medicine.doseSchedules,
                            dosage: medicine.dosage,
                            duration: medicine.duration,
                            frequency: medicine.frequency || 'Daily',
                            selling_price: matchedMedicine.selling_price ?? matchedMedicine.price ?? 0,
                            buying_price: matchedMedicine.buying_price || 0,
                            hsn_code: matchedMedicine.hsn_code || '',
                            gst_percentage: getItemGstPercentage(matchedMedicine),
                            discount: resolvedDiscount,
                            hasConfiguredDiscount: hasPrescriptionDiscount || hasMedicineDiscount,
                            packing: matchedMedicine.packing || '',
                            loose_quantity: matchedMedicine.loose_quantity || 0,
                          });
                        } else {
                          customMedicinesForModal.push({
                            name: medicineName,
                            medicine_name: medicineName,
                            quantity: medicine.quantity || 1,
                            doseSchedules: medicine.doseSchedules,
                            dosage: medicine.dosage,
                            duration: medicine.duration,
                            frequency: medicine.frequency || 'Daily',
                            instructions: medicine.instructions || ''
                          });
                        }
                      } else {
                        customMedicinesForModal.push({
                          name: medicineName,
                          medicine_name: medicineName,
                          quantity: medicine.quantity || 1,
                          doseSchedules: medicine.doseSchedules,
                          dosage: medicine.dosage,
                          duration: medicine.duration,
                          frequency: medicine.frequency || 'Daily',
                          instructions: medicine.instructions || ''
                        });
                      }
                    } catch (searchError) {
                      console.error('Error searching medicine:', searchError);
                      customMedicinesForModal.push({
                        name: medicineName,
                        medicine_name: medicineName,
                        quantity: medicine.quantity || 1,
                        doseSchedules: medicine.doseSchedules,
                        dosage: medicine.dosage,
                        duration: medicine.duration,
                        frequency: medicine.frequency || 'Daily',
                        instructions: medicine.instructions || ''
                      });
                    }
                  }
                  
                  setPendingDbMedicines(dbMedicinesForModal);
                  setPendingCustomMedicines(customMedicinesForModal);
                  setPendingSetFieldValue(() => setFieldValue);
                  setPendingAppointmentDate(appointmentDate);
                  setCustomMedicineModalOpen(true);
                  
                } else {
                  openSnackbar({
                    open: true,
                    message: 'No prescription found for this patient in the last 7 days',
                    variant: 'alert',
                    alert: { color: 'info' }
                  });
                }
              } catch (error) {
                console.error('Error auto-filling prescription:', error);
                openSnackbar({
                  open: true,
                  message: 'Failed to fetch prescription data',
                  variant: 'alert',
                  alert: { color: 'error' }
                });
              }
            };

            // console.log('Form values being passed to InvoiceForm:', {
            //   subtotal,
            //   discountRate,
            //   total,
            //   actualRoundOff,
            //   grandTotalInput,
            //   isGrandTotalMode,
            //   values: values
            // });
            
            return (
              <InvoiceForm
                {...formikProps}
                lists={invoice}
                invoiceMaster={invoiceMaster}
                customerLabel={isSaleBill ? "Wholesaler" : "Customer"}
                tableColumns={[
                  { key: 'no', label: '#' },
                  { key: 'name', label: 'Name' },
                  { key: 'hsn_code', label: 'HSN' },
                  ...(!isCancellationMode ? [
                    { key: 'qty', label: 'Quantity' },
                    ...((variant !== 'pharmacy_bulk_cancel') ? [
                      { key: 'strips', label: 'Strips' },
                      { key: 'pcs', label: 'Pcs' },
                      { key: 'selling_price', label: (isSaleBill) ? (salePriceType === 'buying_price') ? 'Buying Price' : 'Unit Price' : 'Unit Price' },
                      { key: 'discount_in_perc', label: 'Disc. %' },
                      ...(showPharmacyGst ? [{ key: 'gst_percentage', label: variant === 'sale' ? 'GST %' : 'Incl. GST %' }] : []),
                      ...(isInPlaceEditMode ? [{ key: 'cancel_quantity', label: 'Cancelled Qty' }] : []),
                      { key: 'amount', label: 'Amount' },
                    ] : []),
                  ] : []),
                  ...(isCancellationMode ? [
                    { key: 'qty', label: 'Current Quantity' },
                    { key: 'discount_in_perc', label: 'Discount' },
                    { key: 'net_price', label: 'Net Price' }
                  ] : [])
                ]}
                fieldsConfig={[
                  { key: 'name', label: 'Medicine', type: 'autocomplete', fetchOptions: fetchAllItemsDirect, readOnly: isCancellationMode },
                  { key: 'hsn_code', label: 'HSN', type: 'text', readOnly: false },
                  ...(!isCancellationMode ? [
                    { key: 'qty', label: 'Quantity', type: 'number' },
                    ...(
                    (variant !== 'pharmacy_bulk_cancel') ? [
                      { key: 'strips', label: 'Strips', type: 'number', readOnly: true },
                      { key: 'pcs', label: 'Pcs', type: 'number', readOnly: true },
                      {
                        key: 'selling_price',
                        label: (isSaleBill) ? (salePriceType === 'buying_price') ? 'Buying Price' : 'Unit Price' : 'Unit Price',
                        type: 'number',
                        readOnly: false,
                        showEmpty: isSaleBill,
                        disabled: isSaleBill
                      },
                      { key: 'discount_in_perc', label: 'Disc. %', type: 'number', readOnly: false },
                      ...(showPharmacyGst ? [{ key: 'gst_percentage', label: variant === 'sale' ? 'GST %' : 'Incl. GST %', type: 'number', readOnly: variant === 'sale' }] : []),
                      ...(isInPlaceEditMode ? [{ key: 'cancel_quantity', label: 'Cancelled Qty', type: 'number', readOnly: false }] : []),
                      { key: 'amount', label: 'Amount', type: 'amount', readOnly: true }
                    ] : []),
                  ] : []),
                  ...(isCancellationMode ? [
                    { key: 'qty', label: 'Current Quantity', type: 'number', readOnly: true },
                    { key: 'discount_in_perc', label: 'Discount', type: 'number', readOnly: false },
                    { key: 'net_price', label: 'Net Price', type: 'number', readOnly: true }
                  ] : [])
                ]}
                itemTemplate={{ id: UIDV4(), name: '', qty: 1, selling_price: 0, buying_price: 0, strips: 0, pcs: 0, cancel_quantity: 0, discount_in_perc: 0, gst_percentage: 0 }}
                subtotal={subtotal}
                taxRate={taxRate}
                cancelledTotal={cancelledTotal}
                discountRate={discountRate}
                total={total}
                gstSummary={pharmacyGstSummary}
                showGstBreakdown={showPharmacyGst}
                hospitalDetails={selectedHospitalDetails}
                roundOff={actualRoundOff}
                isPositiveRoundOff={isPositiveRoundOff}
                grandTotalInput={grandTotalInput}
                isGrandTotalMode={isGrandTotalMode}
                onGrandTotalChange={handleGrandTotalChange}
                onDiscountChange={handleDiscountChange}
                onMainDiscountFocus={handleMainDiscountFocus}
                onMainDiscountBlur={handleMainDiscountBlur}
                setIsGrandTotalMode={setIsGrandTotalMode}
                setGrandTotalInput={setGrandTotalInput}
                handlePreview={handlerPreview}
                handlerCustomerTo={handlerCustomerTo}
                variant={variant}
                additionalFields={null}
                isCancellationMode={isCancellationMode}
                onCancel={() => handleCancellation(values)}
                showHistoryButton={isCancellationMode}
                invoiceNumber={invoiceId}
                isEditMode={!!invoiceId}
                doctors={doctors}
                showDoctorField={true}
                onPatientSelected={handlePatientSelected}
                // For Sale Bill
                setWholesalerEditModalOpen={setWholesalerEditModalOpen}
                setWholesalerModalOpen={setWholesalerModalOpen}
                setSelectedWholesaler={setSelectedWholesaler}
                selectedWholesaler={selectedWholesaler}
                wholesalersList={wholesalersList}
                salePriceType={salePriceType}
                setSalePriceType={setSalePriceType}
                userDiscount={userDiscount}
                hasUserDiscount={hasUserDiscount}
              />
            );
          }}
        </Formik>
      )}
      {variant !== "sale" && (<>
      <Dialog open={Boolean(walletRefundNotice)} onClose={closeWalletRefundNotice} maxWidth="xs" fullWidth>
        <DialogTitle>Wallet Credited</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ₹{Number(walletRefundNotice?.creditedAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}was credited back to the patient wallet.
          </DialogContentText>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Current wallet balance: ₹{Number(walletRefundNotice?.currentBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWalletRefundNotice} variant="contained">Continue</Button>
        </DialogActions>
      </Dialog>
      <InvoicePrintPreviewModal
        isThermalPrint={isThermalPrint}
        open={openTokenModal}
        onClose={handleModalClose}
        title={`PharmacyInvoice_${medicineOrderResponse?.invoice_number}`}
        reportId={medicineOrderResponse?.invoice_number || medicineOrderResponse?.id}
        patientId={(medicineOrderResponse?.customer || customerInfo)?.id}
        patientName={(medicineOrderResponse?.customer || customerInfo)?.full_name}
        autoSendWhatsAppKey={autoSendWhatsAppKey}
        onAutoSendComplete={() => setAutoSendWhatsAppKey(null)}
      >
        {isThermalPrint ? (
          <PharmacyInvoiceThermalContent 
            customerInfo={medicineOrderResponse?.customer || customerInfo} 
            invoiceData={medicineOrderResponse} 
            hospitalData={hospitalData} 
          />
        ) : (
          <PharmacyInvoiceA4Content 
            customerInfo={medicineOrderResponse?.customer || customerInfo} 
            invoiceData={medicineOrderResponse}
            hospitalData={hospitalData} 
          />
        )}
      </InvoicePrintPreviewModal>
      
      {/* OTP Approval Modal */}
      <OTPApprovalModal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          resetOTPStates();
        }}
        onOTPApproval={handleOTPApproval}
        onSendOTP={handleSendOTP}
        isSubmitting={false}
        isOTPSent={isOTPSent}
        token={otpToken}
        messageId={otpMessageId}
        showDoctorSelection={showDoctorSelection}
      />

      {/* OTP Approval Modal for Cancellation (Discount >50%) */}
      <OTPApprovalModal
        open={cancellationOtpModalOpen}
        onClose={() => {
          setCancellationOtpModalOpen(false);
          resetCancellationOTPStates();
        }}
        onOTPApproval={handleCancellationOTPApproval}
        onSendOTP={handleCancellationSendOTP}
        isSubmitting={false}
        isOTPSent={cancellationIsOTPSent}
        token={cancellationOtpToken}
        messageId={cancellationOtpMessageId}
        showDoctorSelection={cancellationShowDoctorSelection}
        title="Cancellation Approval Required"
        description={cancellationShowDoctorSelection
          ? 'Cancellation requires doctor approval. Please select a doctor and enter OTP.'
          : 'Cancellation requires owner approval. Please enter the OTP sent to the owner.'}
        submitButtonText="Submit OTP & Cancel Items"
        infoText="OTP has been sent. Please enter the OTP to proceed with cancellation."
      />

      {/* OTP Approval Modal for Old Bill (>30 days) */}
      <OTPApprovalModal
        open={oldBillOtpModalOpen}
        onClose={() => {
          resetOldBillOtpStates();
        }}
        onOTPApproval={handleOldBillOtpApproval}
        onSendOTP={async () => {}}
        isSubmitting={oldBillOtpSubmitting}
        isOTPSent={true}
        messageId={oldBillOtpMessageId}
        showDoctorSelection={false}
        title="Old Bill Approval Required"
        description="This bill is beyond the threshold duration. Please enter the OTP sent for cancellation approval."
        submitButtonText="Submit OTP & Cancel Items"
        infoText="OTP has been sent. Please enter the OTP to proceed with cancellation."
        allowResend={false}
      />

      {/* Prescription Auto-Fill Modal */}
      <PrescriptionAutoFillModal
        open={customMedicineModalOpen}
        onClose={handleCloseCustomMedicineModal}
        dbMedicines={pendingDbMedicines}
        customMedicines={pendingCustomMedicines}
        appointmentDate={pendingAppointmentDate}
        onConfirm={handleConfirmPrescriptionAdd}
      />
      </>)}
      {variant === "sale" && (<>
        <UniversalPrintModal
          isThermalPrint={false}
          open={saleBillPrintModal}
          onClose={modalSaleBillCloseHandler}>
          <SaleInvoiceA4Content formData={saleBillItem} />
        </UniversalPrintModal>
      </>)}
      {variant === "pharmacy_bulk_cancel" && (<>
        <UniversalPrintModal
          isThermalPrint={true}
          open={bulkCancelPrintModal}
          onClose={modalBulkCancelCloseHandler}>
          <BulkCancelInvoiceThermalContent formData={bulkCancelItem} />
        </UniversalPrintModal>

      </>)}
    </MainCard></>
  );
};

export default PharmacyInvoicePage;
